export type ChannelKey =
  | 'whatsapp'
  | 'instagram'
  | 'facebook'
  | 'linkedin'
  | 'youtube'
  | 'ads'
  | 'website'
  | 'referral'
  | 'scheduling';

export const CHANNEL_BG: Record<ChannelKey, string> = {
  whatsapp: 'bg-channel-whatsapp',
  instagram: 'bg-channel-instagram',
  facebook: 'bg-channel-facebook',
  linkedin: 'bg-channel-linkedin',
  youtube: 'bg-channel-youtube',
  ads: 'bg-channel-ads',
  website: 'bg-channel-website',
  referral: 'bg-channel-referral',
  scheduling: 'bg-channel-scheduling',
};

export const CHANNEL_STROKE: Record<ChannelKey, string> = {
  whatsapp: 'stroke-channel-whatsapp',
  instagram: 'stroke-channel-instagram',
  facebook: 'stroke-channel-facebook',
  linkedin: 'stroke-channel-linkedin',
  youtube: 'stroke-channel-youtube',
  ads: 'stroke-channel-ads',
  website: 'stroke-channel-website',
  referral: 'stroke-channel-referral',
  scheduling: 'stroke-channel-scheduling',
};

export const CHANNEL_HEX: Record<ChannelKey, string> = {
  whatsapp: '#25D366',
  instagram: '#E1306C',
  facebook: '#1877F2',
  linkedin: '#0A66C2',
  youtube: '#FF0000',
  ads: '#F59E0B',
  website: '#F97316',
  referral: '#8B5CF6',
  scheduling: '#0EB64F',
};

export const LEAD_SOURCE_HEX: Record<ChannelKey, string> = {
  whatsapp: '#22C55E',
  instagram: '#EC4899',
  facebook: '#3B82F6',
  website: '#F59E0B',
  referral: '#8B5CF6',
  linkedin: '#0A66C2',
  youtube: '#FF0000',
  ads: '#F59E0B',
  scheduling: '#14B8A6',
};

export function leadSourceHex(raw: string | null | undefined, fallbackIndex = 0): string {
  const channel = resolveChannel(raw);
  if (channel) return LEAD_SOURCE_HEX[channel];
  return sourceHex(raw, fallbackIndex);
}

const CHART_HEX = [
  '#3B82F6',
  '#22C55E',
  '#8B5CF6',
  '#F59E0B',
  '#EC4899',
  '#14B8A6',
] as const;

export function sourceHex(raw: string | null | undefined, fallbackIndex = 0): string {
  const channel = resolveChannel(raw);
  if (channel) return CHANNEL_HEX[channel];
  const i = ((fallbackIndex % CHART_HEX.length) + CHART_HEX.length) % CHART_HEX.length;
  return CHART_HEX[i];
}

export const CHANNEL_LABEL: Record<ChannelKey, string> = {
  whatsapp: 'WhatsApp',
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  youtube: 'YouTube',
  ads: 'Ads',
  website: 'Website',
  referral: 'Referral',
  scheduling: 'Scheduling',
};

const ALIASES: Record<string, ChannelKey> = {
  whatsapp: 'whatsapp',
  wa: 'whatsapp',
  whats_app: 'whatsapp',
  instagram: 'instagram',
  ig: 'instagram',
  insta: 'instagram',
  facebook: 'facebook',
  fb: 'facebook',
  meta: 'facebook',
  linkedin: 'linkedin',
  li: 'linkedin',
  youtube: 'youtube',
  yt: 'youtube',
  ads: 'ads',
  meta_ads: 'ads',
  google_ads: 'ads',
  paid: 'ads',
  meta_lead_ad: 'ads',
  website: 'website',
  web: 'website',
  site: 'website',
  organic: 'website',
  referral: 'referral',
  refer: 'referral',
  word_of_mouth: 'referral',
  scheduling: 'scheduling',
  schedule: 'scheduling',
  appointment: 'scheduling',
  appointments: 'scheduling',
  booking: 'scheduling',
  bookings: 'scheduling',
};

export function resolveChannel(raw: string | null | undefined): ChannelKey | null {
  if (!raw) return null;
  const key = raw.trim().toLowerCase().replace(/[\s-]+/g, '_');
  return ALIASES[key] ?? null;
}

export function humanizeSource(raw: string | null | undefined): string {
  if (!raw || !raw.trim()) return 'Unknown';
  return raw
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function channelLabel(raw: string | null | undefined): string {
  const channel = resolveChannel(raw);
  if (channel) return CHANNEL_LABEL[channel];
  if (!raw || !raw.trim()) return 'Unknown';
  return raw
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const CHART_BG = [
  'bg-chart-1',
  'bg-chart-2',
  'bg-chart-3',
  'bg-chart-4',
  'bg-chart-5',
  'bg-chart-6',
] as const;

const CHART_STROKE = [
  'stroke-chart-1',
  'stroke-chart-2',
  'stroke-chart-3',
  'stroke-chart-4',
  'stroke-chart-5',
  'stroke-chart-6',
] as const;

export function sourceColorClasses(
  raw: string | null | undefined,
  fallbackIndex = 0
): { bg: string; stroke: string } {
  const channel = resolveChannel(raw);
  if (channel) return { bg: CHANNEL_BG[channel], stroke: CHANNEL_STROKE[channel] };

  const i = ((fallbackIndex % CHART_BG.length) + CHART_BG.length) % CHART_BG.length;
  return { bg: CHART_BG[i], stroke: CHART_STROKE[i] };
}
